import { bcInt, bcLong } from '@lib2/model/util/string/BaseConverter';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import { FileStatusTypeId } from '@lib2/model/media/FileStatusTypeObj';
import { PfileTypeId } from '@lib2/model/pfile/PFileType';
import { getPfiles } from '@lib2/model/pfile/PfileUtil';
import type { PfileRow } from '@lib2/model/pfile/PfileTypes';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import type { ListlistDto, ListlistRow, ListlistOrderedNode } from '@lib2/model/listlist/ListlistDto';
import type {
  ChildListlistOrListId,
  ListlistCreateReq,
  ListlistUpdateReq,
} from '@lib2/model/listlist/ListlistReq';
import { LinkUtils } from '@/features/link/LinkUtils';
import { MediaUtils } from '@/features/media/MediaUtils';
import { formatSmartDate } from '@/shared/utils/date';
import type { SharedLinkConfig } from '@/lib/types/link';
import type { VideoViewData } from '@/lib/types/Media';
import type { StreamViewData, RealtimeStreamData } from '@/lib/types/Stream';

// ── Display model for content tables ──

export interface ContentTableRow {
  llId: number;
  displayId: string;
  title: string;
  isActive: boolean;
  childCount: number;
  created: number;
}

// ── Form data for settings forms ──

export interface ListlistFormData {
  title: string;
  descShort: string;
  mediaStateTypeId: number;
}

// ── Ordered children (design ADR-0014: a Category holds series AND items) ──

/**
 * A parent's ordered children: the ONE operator order across kinds (`children`,
 * the write shape of update-positions) plus the per-kind id projections.
 */
export interface OrderedChildren {
  llIds: number[];
  listIds: number[];
  mediaIds: number[];
  children: ChildListlistOrListId[];
}

/** What a directly placed item is, for chips and messages. */
export type ItemKind = 'video' | 'live';

export interface ItemInfo {
  title: string;
  kind: ItemKind;
  isActive: boolean;
}

export const ITEM_KIND_LABELS: Record<ItemKind, string> = {
  video: 'Video',
  live: 'Live',
};

/** Every kind a category child can be: a series tile, or an item tile that plays. */
export type ChildKind = 'series' | ItemKind;

/**
 * A category's add button and picker title. "Add to category" read as "add a
 * category" (owner, 2026-09-24); the button names what it adds — the header's
 * "Items": series, videos, live streams.
 */
export const CATEGORY_ADD_LABEL = 'Add items';

export const CHILD_KIND_LABELS: Record<ChildKind, string> = {
  series: 'Series',
  video: 'Video',
  live: 'Live stream',
};

export const CHILD_KIND_COLORS: Record<ChildKind, string> = {
  series: 'teal',
  video: 'blue',
  live: 'red',
};

/** Home shows at most this many children of a category row (the server's ceiling). */
export const HOME_ROW_CEILING = 100;

/**
 * One resolved child of a category, ready to render: the row in the editor,
 * the tile in the Home-row preview, the chip in the picker tray. Built once
 * per data change (AppContentUtils.buildCategoryChildViews), never per render.
 */
export interface CategoryChildView {
  /** Kind-qualified key (childKey) — the sortable id. */
  key: string;
  ref: ChildListlistOrListId;
  kind: ChildKind;
  title: string;
  /** "Series · 3 seasons" / "01:02:03 · Sep 7" / null. */
  subtitle: string | null;
  thumbnailUrl: string | null;
  isActive: boolean;
  /** The child is no longer in the account (deleted media, missing series); Remove still works. */
  unavailable: boolean;
  /** Where "open" goes; null when unavailable. */
  href: string | null;
  /** A live stream that is on air right now (decoration only). */
  isOnAir: boolean;
}

/** One candidate the picker offers (a series, a video or a live stream). */
export interface PickCandidate {
  key: string;
  ref: ChildListlistOrListId;
  kind: ChildKind;
  title: string;
  subtitle: string | null;
  thumbnailUrl: string | null;
  isActive: boolean;
  isOnAir: boolean;
  /** The lower-cased title, prepared once per source for fuzzysort. */
  searchTitle: string;
}

export interface CategoryChildContext {
  seriesById: Map<number, ListlistDto>;
  /** llId → number of seasons (child lists) from the positions tree's seriesNodes. */
  seasonCountByLlId: Map<number, number>;
  videosById: Map<number, VideoViewData>;
  streamsById: Map<number, StreamViewData>;
  linkConfig: SharedLinkConfig | null;
  liveStatusMap: Map<number, RealtimeStreamData>;
}

export class AppContentUtils {
  // ── ID Conversions (llId uses bcInt) ──

  static getDisplayId(llId: number): string {
    return bcInt.getStr(llId);
  }

  static getNumericId(displayId: string): number {
    return bcInt.getNum(displayId);
  }

  /** Media ids are 64-bit — the Videos/Streams grids show them through bcLong. */
  static getMediaDisplayId(mediaId: number): string {
    return bcLong.getStr(mediaId);
  }

  // ── Child references ──

  /** A ref with every key present (the wire tolerates absent; the UI compares whole refs). */
  static normalizeChildRef(ref: ChildListlistOrListId): ChildListlistOrListId {
    return {
      childLLId: ref.childLLId ?? null,
      childListId: ref.childListId ?? null,
      childMediaId: ref.childMediaId ?? null,
    };
  }

  static llRef(llId: number): ChildListlistOrListId {
    return { childLLId: llId, childListId: null, childMediaId: null };
  }

  static listRef(listId: number): ChildListlistOrListId {
    return { childLLId: null, childListId: listId, childMediaId: null };
  }

  static mediaRef(mediaId: number): ChildListlistOrListId {
    return { childLLId: null, childListId: null, childMediaId: mediaId };
  }

  /**
   * A stable, kind-qualified key for a child ref. Listlist ids and media ids
   * come from different id sequences and MAY collide numerically, so a sortable
   * list keyed by bare ids would merge a series with a video.
   */
  static childKey(ref: ChildListlistOrListId): string {
    if (ref.childLLId != null) return `ll:${ref.childLLId}`;
    if (ref.childListId != null) return `list:${ref.childListId}`;
    if (ref.childMediaId != null) return `m:${ref.childMediaId}`;
    return 'none';
  }

  static isSameChild(a: ChildListlistOrListId, b: ChildListlistOrListId): boolean {
    return AppContentUtils.childKey(a) === AppContentUtils.childKey(b);
  }

  // ── Table row builder ──

  static buildTableRow(
    dto: ListlistDto,
    childCount: number,
  ): ContentTableRow {
    return {
      llId: dto.llr.llId,
      displayId: AppContentUtils.getDisplayId(dto.llr.llId),
      title: dto.llr.title,
      isActive: dto.llr.mediaStateTypeId === MediaStateTypeId.Active,
      childCount,
      created: dto.llr.created,
    };
  }

  // ── DTO → Form data ──

  static transformToFormData(llr: ListlistRow): ListlistFormData {
    return {
      title: llr.title,
      descShort: llr.descShort ?? '',
      mediaStateTypeId: llr.mediaStateTypeId,
    };
  }

  // ── Form data → Create request ──

  static buildCreateReq(
    title: string,
    typeId: ListlistTypeId,
  ): ListlistCreateReq {
    return {
      listlistTypeId: typeId,
      mediaStateTypeId: MediaStateTypeId.Active,
      title,
      descShort: null,
    };
  }

  // ── Form data → Update request ──

  static buildUpdateReq(form: ListlistFormData): ListlistUpdateReq {
    return {
      mediaStateTypeId: form.mediaStateTypeId,
      title: form.title,
      descShort: form.descShort || null,
    };
  }

  // ── Child count from positions graph ──

  /**
   * Build a map of llId → direct child count from a positions tree.
   * Counts childListLists (listlists) + childListIds (playlists/lists) +
   * childMediaIds (a category's directly placed items).
   */
  static buildChildCountMap(
    rootNodes: ListlistOrderedNode[],
  ): Map<number, number> {
    const map = new Map<number, number>();

    function walk(node: ListlistOrderedNode) {
      map.set(
        node.llId,
        node.childListLists.length + node.childListIds.length + (node.childMediaIds?.length ?? 0),
      );
      for (const child of node.childListLists) {
        walk(child);
      }
    }

    for (const root of rootNodes) {
      walk(root);
    }
    return map;
  }

  /**
   * A node's children in the operator's ONE order. Post-ADR payloads carry
   * `children` (position order across kinds); a pre-ADR payload lacks it, and
   * the order is then derived per kind: listlists first, then lists.
   */
  static orderedChildRefs(node: ListlistOrderedNode): ChildListlistOrListId[] {
    if (node.children) {
      return node.children.map(AppContentUtils.normalizeChildRef);
    }
    return [
      ...node.childListLists.map((c) => AppContentUtils.llRef(c.llId)),
      ...node.childListIds.map((id) => AppContentUtils.listRef(id)),
    ];
  }

  /**
   * Get ordered children for a specific parent from a positions tree.
   *
   * FIRST-MATCH-WINS: the same llId can appear in multiple positions arrays with
   * DIFFERENT children (e.g. a series is a childless leaf inside rootNodes trees
   * but carries its seasons in seriesNodes). Callers must pass only the array
   * that is authoritative for the parent's level: rootNodes for content groups,
   * categoryNodes for categories, seriesNodes for series.
   */
  static getOrderedChildren(
    parentLlId: number,
    rootNodes: ListlistOrderedNode[],
  ): OrderedChildren | null {
    function find(node: ListlistOrderedNode): ListlistOrderedNode | null {
      if (node.llId === parentLlId) return node;
      for (const child of node.childListLists) {
        const found = find(child);
        if (found) return found;
      }
      return null;
    }

    for (const root of rootNodes) {
      const found = find(root);
      if (found) {
        const children = AppContentUtils.orderedChildRefs(found);
        return {
          llIds: children.flatMap((c) => (c.childLLId != null ? [c.childLLId] : [])),
          listIds: children.flatMap((c) => (c.childListId != null ? [c.childListId] : [])),
          mediaIds: children.flatMap((c) => (c.childMediaId != null ? [c.childMediaId] : [])),
          children,
        };
      }
    }
    return null;
  }

  // ── Child type label helpers ──

  /** The generic noun for a parent's children (a category's are mixed: series and items). */
  static getChildTypeLabel(parentType: ListlistTypeId): string {
    switch (parentType) {
      case ListlistTypeId.Root:
        return 'Category';
      case ListlistTypeId.Category:
        return 'Item';
      case ListlistTypeId.Series:
        return 'Season';
      default:
        return 'Item';
    }
  }

  static getChildTypeLabelPlural(parentType: ListlistTypeId): string {
    switch (parentType) {
      case ListlistTypeId.Root:
        return 'Categories';
      case ListlistTypeId.Category:
        return 'Items';
      case ListlistTypeId.Series:
        return 'Seasons';
      default:
        return 'Items';
    }
  }

  /** The noun for ONE child ref under a parent, for add/remove messages. */
  static getChildRefLabel(parentType: ListlistTypeId, ref: ChildListlistOrListId, item?: ItemInfo): string {
    if (ref.childMediaId != null) {
      return item?.kind === 'live' ? 'Live stream' : item ? 'Video' : 'Item';
    }
    if (ref.childListId != null) return 'Season';
    if (parentType === ListlistTypeId.Category) return 'Series';
    return AppContentUtils.getChildTypeLabel(parentType);
  }

  // ── Category child views (design ADR-0014: series and items in one order) ──

  /**
   * A listlist's 16:9 art the way the category/series pages show it: the
   * ready thumb rendition when one exists, else the ready poster (the same
   * pfiles the PfileImageUploader reads; URLs come from LinkUtils, never
   * hand-built).
   */
  static listlistThumbnailUrl(linkConfig: SharedLinkConfig | null, files: PfileRow[] | undefined): string | null {
    if (!linkConfig || !files || files.length === 0) return null;
    const ready = (types: PfileTypeId[]) =>
      getPfiles(files, types).find((f) => f.fileStatusTypeId === FileStatusTypeId.Ready && !f.deleted) ?? null;
    const pfile = ready([PfileTypeId.ListThumb]) ?? ready([PfileTypeId.ListPoster]);
    return pfile ? LinkUtils.getAccountFileUrl(linkConfig, pfile.fileId, pfile.format) : null;
  }

  /** The Videos grid's thumb rendition for a video or a stream (poster uploads carry a companion thumb). */
  static mediaThumbnailUrl(
    linkConfig: SharedLinkConfig | null,
    mediaId: number,
    files: VideoViewData['files'] | undefined,
  ): string | null {
    if (!linkConfig) return null;
    const thumbFile = MediaUtils.getThumbnailFile(files ?? []);
    return thumbFile ? LinkUtils.getMediaFileUrl(linkConfig, mediaId, thumbFile.fileId, thumbFile.format) : null;
  }

  /** "Series · 3 seasons" — the same line the app's hero renders for a series. */
  static seriesSubtitle(seasonCount: number): string {
    if (seasonCount <= 0) return 'Series';
    return `Series · ${seasonCount} ${seasonCount === 1 ? 'season' : 'seasons'}`;
  }

  /** "01:02:03 · Sep 7" — duration and published date with the grids' own formatters; null when neither is known. */
  static videoSubtitle(durationSeconds: number, publishedEpoch: number): string | null {
    const parts: string[] = [];
    if (durationSeconds > 0) parts.push(MediaUtils.formatDuration(durationSeconds));
    if (publishedEpoch > 0) parts.push(formatSmartDate(publishedEpoch));
    return parts.length ? parts.join(' · ') : null;
  }

  /** llId → season count from the positions tree's seriesNodes (a series' seasons are its child lists). */
  static seasonCountsFromSeriesNodes(seriesNodes: ListlistOrderedNode[] | undefined): Map<number, number> {
    const map = new Map<number, number>();
    for (const node of seriesNodes ?? []) {
      map.set(node.llId, node.childListIds.length);
    }
    return map;
  }

  /** Resolve a category's ordered refs into renderable rows, in order. */
  static buildCategoryChildViews(refs: ChildListlistOrListId[], ctx: CategoryChildContext): CategoryChildView[] {
    return refs.map((ref) => AppContentUtils.buildCategoryChildView(ref, ctx));
  }

  static buildCategoryChildView(ref: ChildListlistOrListId, ctx: CategoryChildContext): CategoryChildView {
    const key = AppContentUtils.childKey(ref);
    if (ref.childMediaId != null) {
      const mediaId = ref.childMediaId;
      const stream = ctx.streamsById.get(mediaId);
      if (stream) {
        const media = stream.media;
        return {
          key,
          ref,
          kind: 'live',
          title: media.title,
          subtitle: null,
          thumbnailUrl: AppContentUtils.mediaThumbnailUrl(ctx.linkConfig, mediaId, stream.files),
          isActive: media.mediaStateType === 'active',
          unavailable: false,
          href: `/streams/${bcLong.getStr(mediaId)}`,
          isOnAir: ctx.liveStatusMap.has(mediaId),
        };
      }
      const video = ctx.videosById.get(mediaId);
      if (video) {
        const media = video.media;
        return {
          key,
          ref,
          kind: 'video',
          title: media.title,
          subtitle: AppContentUtils.videoSubtitle(media.duration, media.published),
          thumbnailUrl: AppContentUtils.mediaThumbnailUrl(ctx.linkConfig, mediaId, video.files),
          isActive: media.mediaStateType === 'active',
          unavailable: false,
          href: `/videos/${bcLong.getStr(mediaId)}`,
          isOnAir: false,
        };
      }
      return {
        key,
        ref,
        kind: 'video',
        title: 'Unavailable — this video or stream is no longer in your library',
        subtitle: bcLong.getStr(mediaId),
        thumbnailUrl: null,
        isActive: false,
        unavailable: true,
        href: null,
        isOnAir: false,
      };
    }
    const llId = ref.childLLId;
    const series = llId != null ? ctx.seriesById.get(llId) : undefined;
    if (llId != null && series) {
      return {
        key,
        ref,
        kind: 'series',
        title: series.llr.title,
        subtitle: AppContentUtils.seriesSubtitle(ctx.seasonCountByLlId.get(llId) ?? 0),
        thumbnailUrl: AppContentUtils.listlistThumbnailUrl(ctx.linkConfig, series.files),
        isActive: series.llr.mediaStateTypeId === MediaStateTypeId.Active,
        unavailable: false,
        href: `/apps/content/series/${bcInt.getStr(llId)}`,
        isOnAir: false,
      };
    }
    return {
      key,
      ref,
      kind: 'series',
      title: 'Unavailable — this series no longer exists',
      subtitle: llId != null ? bcInt.getStr(llId) : null,
      thumbnailUrl: null,
      isActive: false,
      unavailable: true,
      href: null,
      isOnAir: false,
    };
  }

  // ── Picker candidates ──

  static seriesCandidate(series: ListlistDto, ctx: Pick<CategoryChildContext, 'linkConfig' | 'seasonCountByLlId'>): PickCandidate {
    const llId = series.llr.llId;
    return {
      key: `ll:${llId}`,
      ref: AppContentUtils.llRef(llId),
      kind: 'series',
      title: series.llr.title,
      subtitle: AppContentUtils.seriesSubtitle(ctx.seasonCountByLlId.get(llId) ?? 0),
      thumbnailUrl: AppContentUtils.listlistThumbnailUrl(ctx.linkConfig, series.files),
      isActive: series.llr.mediaStateTypeId === MediaStateTypeId.Active,
      isOnAir: false,
      searchTitle: series.llr.title,
    };
  }

  static videoCandidate(video: VideoViewData, linkConfig: SharedLinkConfig | null): PickCandidate {
    const media = video.media;
    return {
      key: `m:${media.mediaId}`,
      ref: AppContentUtils.mediaRef(media.mediaId),
      kind: 'video',
      title: media.title,
      subtitle: AppContentUtils.videoSubtitle(media.duration, media.published),
      thumbnailUrl: AppContentUtils.mediaThumbnailUrl(linkConfig, media.mediaId, video.files),
      isActive: media.mediaStateType === 'active',
      isOnAir: false,
      searchTitle: media.title,
    };
  }

  static streamCandidate(
    stream: StreamViewData,
    linkConfig: SharedLinkConfig | null,
    liveStatusMap: Map<number, RealtimeStreamData>,
  ): PickCandidate {
    const media = stream.media;
    return {
      key: `m:${media.mediaId}`,
      ref: AppContentUtils.mediaRef(media.mediaId),
      kind: 'live',
      title: media.title,
      subtitle: null,
      thumbnailUrl: AppContentUtils.mediaThumbnailUrl(linkConfig, media.mediaId, stream.files),
      isActive: media.mediaStateType === 'active',
      isOnAir: liveStatusMap.has(media.mediaId),
      searchTitle: media.title,
    };
  }

  /** "Add 3 items" / "Add 1 video" / "Add 2 live streams" / "Add 2 series" — one noun when the picks share a kind. */
  static pickCtaLabel(kinds: ChildKind[]): string {
    const n = kinds.length;
    if (n === 0) return CATEGORY_ADD_LABEL;
    const uniform = kinds.every((k) => k === kinds[0]);
    if (!uniform) return `Add ${n} items`;
    const noun =
      kinds[0] === 'series' ? 'series' : kinds[0] === 'video' ? (n === 1 ? 'video' : 'videos') : n === 1 ? 'live stream' : 'live streams';
    return `Add ${n} ${noun}`;
  }

  /** What "Added …" says afterwards, same nouns as the CTA. */
  static addedToastMessage(kinds: ChildKind[]): string {
    const label = AppContentUtils.pickCtaLabel(kinds); // "Add 3 items"
    return label.replace(/^Add /, 'Added ');
  }

  /**
   * The full order after placing picks at the TOP: the picks first, in pick
   * order, then every current child. The server appends new edges at the end,
   * so "Top" is one update-positions call with this order after the adds.
   */
  static withPicksAtTop(current: ChildListlistOrListId[], picks: ChildListlistOrListId[]): ChildListlistOrListId[] {
    const pickKeys = new Set(picks.map(AppContentUtils.childKey));
    return [...picks, ...current.filter((c) => !pickKeys.has(AppContentUtils.childKey(c)))];
  }
}
